/**
 * LearnHub - Scout Intelligence Library
 * 
 * DEX Protocol: Premium Minimalism, 5-Word Filter for UI chrome
 * Focus: How platform works, why it's useful, real use cases
 */

import React from "react";
import { Link } from "react-router-dom";
import LearnLayout from "./LearnLayout";

// SVG Icons (DEX: No emojis, custom SVGs only)
const SearchIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/>
    <path d="m21 21-4.35-4.35"/>
  </svg>
);

const UsersIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

const BuildingIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="2" width="16" height="20" rx="2" ry="2"/>
    <path d="M9 22v-4h6v4"/>
    <path d="M8 6h.01"/>
    <path d="M16 6h.01"/>
    <path d="M12 6h.01"/>
    <path d="M12 10h.01"/>
    <path d="M12 14h.01"/>
    <path d="M16 10h.01"/>
    <path d="M16 14h.01"/>
    <path d="M8 10h.01"/>
    <path d="M8 14h.01"/>
  </svg>
);

const ZapIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
  </svg>
);

const ArrowRightIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/>
    <polyline points="12 5 19 12 12 19"/>
  </svg>
);

// How It Works steps
const howItWorks = [
  {
    step: "01",
    title: "Describe",
    description: "Type what you're looking for in plain English. No Boolean, no filters to configure.",
    example: '"Series B healthcare startups in Boston"',
  },
  {
    step: "02",
    title: "Scout Searches",
    description: "Our AI interprets your intent and searches across millions of profiles and companies.",
    example: "Results in under 5 seconds",
  },
  {
    step: "03",
    title: "Review & Act",
    description: "Browse ranked results. Save the ones you like. Export to your CRM or ATS.",
    example: "One-click export",
  },
];

// Use cases - real, practical examples
const useCases = [
  {
    title: "Find Companies",
    description: "Search by industry, size, location, funding stage, technology stack, or growth signals.",
    icon: BuildingIcon,
    examples: [
      "Fintech companies in NYC with 50-200 employees",
      "Manufacturing firms that recently expanded",
      "SaaS startups using AWS",
    ],
  },
  {
    title: "Find People",
    description: "Search by role, experience, skills, company background, or career trajectory.",
    icon: UsersIcon,
    examples: [
      "VP of Sales from Fortune 500 companies",
      "Engineers with Python and ML experience",
      "CFOs in healthcare with IPO experience",
    ],
  },
  {
    title: "Track Signals",
    description: "Monitor funding rounds, executive moves, expansions, and other business signals.",
    icon: ZapIcon,
    examples: [
      "Companies that raised Series A last quarter",
      "Executives who changed roles recently",
      "Firms opening new offices",
    ],
  },
];

// Why Scout section
const whyScout = [
  {
    title: "Natural Language",
    description: "Search like you think. No complex filters or Boolean operators to learn.",
  },
  {
    title: "Fast Results",
    description: "Get relevant matches in seconds, not hours of manual searching.",
  },
  {
    title: "Save & Export",
    description: "Build lists, save favorites, and export to your existing tools.",
  },
  {
    title: "Always Fresh",
    description: "Data is continuously updated so you see the latest information.",
  },
];

export default function LearnHub() {
  return (
    <LearnLayout 
      pageTitle="Learn"
      pageDescription="How Scout works and how to get the most out of it"
    >
      {/* Hero - Simple and clean */}
      <section className="bg-white py-16 lg:py-20 border-b border-black/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 
            className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-black mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            How Scout Works
          </h1>
          <p 
            className="text-lg text-black/60 max-w-2xl mx-auto"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Scout helps you find companies and people using natural language. 
            Just describe what you're looking for.
          </p>
        </div>
      </section>

      {/* How It Works - 3 steps */}
      <section className="py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 
            className="text-2xl font-semibold text-black mb-12 text-center"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Three Simple Steps
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {howItWorks.map((item, index) => (
              <div 
                key={index}
                className="text-center"
              >
                <div 
                  className="text-4xl font-light text-black/10 mb-4"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {item.step}
                </div>
                <h3 
                  className="text-lg font-semibold text-black mb-2"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {item.title}
                </h3>
                <p 
                  className="text-sm text-black/60 mb-3"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {item.description}
                </p>
                <p 
                  className="text-xs text-black/40 italic"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {item.example}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="bg-black/[0.02] py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 
            className="text-2xl font-semibold text-black mb-4 text-center"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            What You Can Search
          </h2>
          <p 
            className="text-black/50 text-center mb-12 max-w-xl mx-auto"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Scout understands natural language queries for companies, people, and business signals.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {useCases.map((useCase, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl border border-black/5 p-6"
              >
                <div className="w-10 h-10 rounded-lg bg-[#5a20ff]/5 flex items-center justify-center mb-4">
                  <useCase.icon className="w-5 h-5 text-[#5a20ff]/60" />
                </div>
                <h3 
                  className="text-base font-semibold text-black mb-2"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {useCase.title}
                </h3>
                <p 
                  className="text-sm text-black/50 mb-4"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {useCase.description}
                </p>
                <div className="space-y-2">
                  {useCase.examples.map((example, i) => (
                    <div 
                      key={i}
                      className="text-xs text-black/40 flex items-start gap-2"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      <SearchIcon className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      <span>{example}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Scout */}
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 
            className="text-2xl font-semibold text-black mb-12 text-center"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Why Use Scout
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {whyScout.map((item, index) => (
              <div 
                key={index}
                className="flex gap-4"
              >
                <div className="w-1 h-full bg-[#5a20ff]/10 rounded-full flex-shrink-0" />
                <div>
                  <h3 
                    className="text-base font-semibold text-black mb-1"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {item.title}
                  </h3>
                  <p 
                    className="text-sm text-black/50"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA - Simple */}
      <section className="border-t border-black/5 py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-xl font-semibold text-black mb-3"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Ready to try it?
          </h2>
          <p 
            className="text-black/50 mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Start searching in seconds.
          </p>
          <Link
            to="/app"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#5a20ff]/8 text-[#5a20ff]/80 rounded-lg text-sm font-medium hover:bg-[#5a20ff]/12 hover:text-[#5a20ff] transition-all duration-200"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Open Scout
            <ArrowRightIcon className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </LearnLayout>
  );
}
